import { useState, useEffect } from "react";
import { PaymentService, PaymentMethodVM, PlaidPaymentMethodVM } from "@propertelligent/client-api";
import PaymentAccountsTopHeader from "./PaymentAccountsTopHeader";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import PaymentAccountCardItem from "./PaymentAccountCardItem";
import { useDebounce } from "use-debounce";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../newComponents/form-fields/InputField";
import Radio from "../../../newComponents/form-fields/radio";
import { PaymentMethodTypesEnum, PaymentMethodEntityTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import RadioGroup from "../../../newComponents/form-fields/RadioGroup";
import { GetImages } from "../../../utils/GetImages";
import AddPlaidAccount from "../../../components/firstTimeUserExperience/AddPlaidAccount";
import {
  QuickstartProvider,
  PlaidAccountDetails,
} from "../../../components/firstTimeUserExperience/Context";
import PlaidScreen from "../../../newComponents/PlaidScreen/PlaidScreen";
import TextButton from "../../../newComponents/buttons/text-button";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";

interface PaymentAccountsProps {
  className?: string;
}

const PaymentAccountsPage = ({ className = "" }: PaymentAccountsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isReconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [reconnectPaymentMethod, setReconnectPaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodVM | undefined>(undefined);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const check = usePermissions();
  const canWrite = check.canWrite("PaymentAccounts");

  // For Empty State
  const [hasAnyPaymentAccounts, setHasAnyPaymentAccounts] = useState<boolean>(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const handleFilterChange = (filter: string) => {
    updateFullyQualifiedFilter(filter, searchTerm);
  };

  const updateFullyQualifiedFilter = (filter: string, search: string) => {
    let baseFilter = "";

    switch (filter) {
      case "ACH":
        baseFilter = `PaymentMethodTypeId == ${PaymentMethodTypesEnum.ACH}`;
        break;
      case "CreditCard":
        baseFilter = `PaymentMethodTypeId == ${PaymentMethodTypesEnum.CreditCard}`;
        break;
      case "All":
      default:
        baseFilter = "";
        break;
    }

    const searchFilter = search
      ? `AccountName.ToLower().Contains("${search.toLowerCase()}")`
      : "";

    const combinedFilter = [baseFilter, searchFilter].filter(Boolean).join(" &&");
    setFullyQualifiedFilter(combinedFilter || null);
  };



  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
    aggregateValues,
  } = infiniteScrollHook({
    httpFetchDataFn: PaymentService.getApiPaymentMethodOrganizationManageList,
    FullyQualifiedFilters: fullyQualifiedFilter,
    pageSize: 10,
  });

  useEffect(() => {
    updateFullyQualifiedFilter(activeFilter, debouncedSearchTerm);
  }, [debouncedSearchTerm, activeFilter]);

  useEffect(() => {
    if (rowCount > 0) {
      setHasAnyPaymentAccounts(true);
    }
  }, [rowCount]);



  const methods = useForm<PaymentMethodVM>({
    defaultValues: {
      id: undefined,
      accountName: "",
      paymentMethodTypeId: PaymentMethodTypesEnum.ACH, // Ensure default value is set
      entityTypeId: PaymentMethodEntityTypesEnum.Organization,
      entityId: organizationId,
      achPaymentMethod: {
        accountNumber: "",
        routingNumber: "",
      },
      creditCardPaymentMethod: {
        cardHolderName: "",
        cardNumber: "",
        cvvNumber: "",
        billingZipCode: "",
        expMonth: undefined,
        expYear: undefined,
      },
    },
    mode: "onBlur",
  });


  useEffect(() => {
    if (isAddModalOpen) {
      if (selectedPaymentMethod) {
        
        methods.reset(selectedPaymentMethod);
      } else {
        methods.reset({
          id: undefined,
          accountName: "",
          paymentMethodTypeId: PaymentMethodTypesEnum.ACH,   
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          achPaymentMethod: {
            accountNumber: "",
            routingNumber: "",
          },
          creditCardPaymentMethod: {
            cardHolderName: "",
            cardNumber: "",
            cvvNumber: "",
            billingZipCode: "",
            expMonth: undefined,
            expYear: undefined,
          },
        });
      }
    }
  }, [isAddModalOpen, selectedPaymentMethod, methods, organizationId]);

  const { handleSubmit, watch, reset, formState: { isDirty } } = methods;
  const selectedPaymentMethodType = Number(watch("paymentMethodTypeId"));

  const onSubmit = async (data: PaymentMethodVM) => {

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let payload;
      if (selectedPaymentMethodType === PaymentMethodTypesEnum.ACH) {
        payload = {
          id: data.id,
          accountName: data.accountName,
          paymentMethodTypeId: PaymentMethodTypesEnum.ACH,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          achPaymentMethod: {
            accountNumber: data.achPaymentMethod?.accountNumber,
            routingNumber: data.achPaymentMethod?.routingNumber,
          },
          creditCardPaymentMethod: null,
        };
      } else {
        payload = {
          id: data.id,
          accountName: data.accountName,
          paymentMethodTypeId: PaymentMethodTypesEnum.CreditCard,
          entityTypeId: PaymentMethodEntityTypesEnum.Organization,
          entityId: organizationId,
          creditCardPaymentMethod: {
            cardNumber: data.creditCardPaymentMethod?.cardNumber?.split("").join(""),
            cvvNumber: data.creditCardPaymentMethod?.cvvNumber,
            billingZipCode: data.creditCardPaymentMethod?.billingZipCode,
            expMonth: data.creditCardPaymentMethod?.expMonth,
            expYear: data.creditCardPaymentMethod?.expYear,
          },
          achPaymentMethod: null,
        };
      }

      const apiCall = data.id
        ? PaymentService.putApiPaymentMethod
        : PaymentService.postApiPaymentMethod;

      const res = await apiHelper(
        apiCall,
        {
          title: data.id ? "Payment Account Updated" : "Payment Account Added",
          successMessage: data.id
            ? "Account updated successfully"
            : "Account added successfully",
          failureMessage: data.id
            ? "Unable to update payment account"
            : "Unable to add payment account",
          showNotification: true,
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />

        },
        payload
      );

      if (res?.data?.success) {
        setAddModalOpen(false);
        reset();
        fetchData(1);
      }
    } catch (error) {
      console.error("Error submitting payment method:", error);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPaymentMethod = (paymentMethod: PaymentMethodVM) => {
    setSelectedPaymentMethod(paymentMethod);
    methods.reset(paymentMethod);
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedPaymentMethod(undefined);
    reset({
      id: undefined,
      accountName: "",
      paymentMethodTypeId: PaymentMethodTypesEnum.ACH,
      entityTypeId: PaymentMethodEntityTypesEnum.Organization,
      entityId: organizationId,
      achPaymentMethod: {
        accountNumber: "",
        routingNumber: "",
      },
      creditCardPaymentMethod: {
        cardNumber: "",
        cvvNumber: "",
        billingZipCode: "",
        expMonth: undefined,
        expYear: undefined,
      },
    });
  };

  // Handle Plaid account success - payment method is auto-saved by Link component
  const handlePlaidSuccess = (plaidData: PlaidAccountDetails) => {
    setAddModalOpen(false);
    setReconnectModalOpen(false);
    setReconnectPaymentMethod(null);
    reset();
    fetchData(1);
  };

  const handleReconnectPlaid = (paymentMethod: PaymentMethodVM) => {
    setReconnectPaymentMethod(paymentMethod);
    setReconnectModalOpen(true);
  };

  const formId = selectedPaymentMethod ? "edit-payment-method-form" : "add-payment-method-form";

  return (
    <PermissionGuard permission="PaymentAccounts" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`payment-accounts-page content-wrapper container-fluid ${className}`}>
        {hasAnyPaymentAccounts && (
          <PaymentAccountsTopHeader
            PageHeading="Payment Accounts"
            Count={rowCount}
            ButtonName={canWrite ? "Add Payment Account" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleFilterChange={handleFilterChange}
            aggregateValuesFilters={aggregateValues}
            onClick={() => {
              setSelectedPaymentMethod(undefined);
              reset();
              setAddModalOpen(true);
            }}
          />
        )}

        <PaymentAccountCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
          }}
          onEditPaymentMethod={canWrite ? handleEditPaymentMethod : undefined}
          onReconnectPlaid={canWrite ? handleReconnectPlaid : undefined}
          className="organisation-listing-card"
          hasAnyPaymentAccounts={hasAnyPaymentAccounts}
        />

        <RightSideModal
          isOpen={isReconnectModalOpen}
          onClose={() => {
            setReconnectModalOpen(false);
            setReconnectPaymentMethod(null);
          }}
          title="Reconnect Plaid Account"
          maxWidth={500}
          buttonlabel={undefined}
          formId={undefined}
          isSubmitDisabled={isSubmitting}
          customFooter={
            <div className="d-flex justify-content-end gap-2">
              <TextButton
                label="Cancel"
                onClick={() => {
                  setReconnectModalOpen(false);
                  setReconnectPaymentMethod(null);
                }}
              />
              {reconnectPaymentMethod && (
                <QuickstartProvider
                  plaidReconnectPaymentMethod={reconnectPaymentMethod}
                  plaidButtonLabel="Reconnect"
                >
                  <AddPlaidAccount
                    onPlaidSuccess={handlePlaidSuccess}
                    showFinishButton={false}
                  />
                </QuickstartProvider>
              )}
            </div>
          }
        >
          <Card className="no-bg">
            <PlaidScreen />
          </Card>
        </RightSideModal>

        <RightSideModal
          isOpen={isAddModalOpen}
          onClose={handleModalClose}
          title={selectedPaymentMethod ? "Edit Payment Method" : "Add Payment Method"}
          buttonlabel={selectedPaymentMethod ? "Update Payment Method" : "Add Payment Method"}
          formId={Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.Plaid ? undefined : formId}
          maxWidth={550}
          isDirty={isDirty}
          customFooter={
            Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.Plaid ? (
              <div className="d-flex justify-content-end gap-2">
                <TextButton label={"Cancel"} onClick={handleModalClose} />
                <QuickstartProvider
                  plaidEntityConfig={{
                    entityTypeId: PaymentMethodEntityTypesEnum.Organization,
                    entityId: organizationId,
                  }}
                >
                  <AddPlaidAccount
                    onPlaidSuccess={handlePlaidSuccess}
                    showFinishButton={false}
                  />
                </QuickstartProvider>
              </div>
            ) : undefined
          }
        >
          <Card className="no-bg">
            <FormProvider {...methods}>

              <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {

                console.log("Form validation errors:", error); // Log errors to console
              })}>
                <div className="row">
                  <div className="col-12">

                    <RadioGroup
                      label="Payment Method"
                      required
                      error={methods.formState.errors.paymentMethodTypeId}
                    >
                      <Radio
                        name="paymentMethodTypeId"
                        value={PaymentMethodTypesEnum.ACH}
                        label="ACH"
                        requiredMessage="Payment method is required"
                      />
                      <Radio
                        name="paymentMethodTypeId"
                        value={PaymentMethodTypesEnum.CreditCard}
                        label="Credit Card"
                        requiredMessage="Payment method is required"
                      />
                      <Radio
                        name="paymentMethodTypeId"
                        value={PaymentMethodTypesEnum.Plaid}
                        label="Plaid"
                        requiredMessage="Payment method is required"
                      />
                    </RadioGroup>

                  </div>

                  {/* Show Account Name field only for ACH and Credit Card */}
                  {Number(selectedPaymentMethodType) !== PaymentMethodTypesEnum.Plaid && (
                    <div className="col-12">
                      <InputField
                        name="accountName"
                        label="Account Name"
                        type="text"
                        required
                        placeholder="Enter Account Name"
                        validation={{
                          required: "Account name is required",
                          minLength: {
                            value: 1,
                            message: "Account name is required",
                          },
                          maxLength: {
                            value: 30,
                            message: "Account name cannot exceed 30 characters",
                          },
                        }}
                      />
                    </div>
                  )}

                  {/* Plaid Account Section */}
                  {Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.Plaid && (
                    <div className="col-12">
                      <PlaidScreen />
                    </div>
                  )}

                  {Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.ACH && (
                    <>
                      <div className="col-md-6">
                        <InputField
                          name="achPaymentMethod.accountNumber"
                          label="Account Number"
                          type="text"
                          required
                          placeholder="Enter Account Number"
                          allowSpecialChars={false}
                          validation={{
                            required: "Account number is required",
                            minLength: {
                              value: 1,
                              message: "Account number is required",
                            },
                            maxLength: {
                              value: 50,
                              message: "Account number cannot exceed 50 characters",
                            },
                          }}

                        />
                      </div>
                      <div className="col-md-6">
                        <InputField
                          name="achPaymentMethod.routingNumber"
                          label="Routing Number"
                          type="text"
                          required
                          requiredMessage="Routing number is required"
                          placeholder="Enter Routing Number"
                          allowSpecialChars={false}
                          onlyNumeric={true}
                        />
                      </div>
                    </>
                  )}

                  {Number(selectedPaymentMethodType) === PaymentMethodTypesEnum.CreditCard && (
                    <>

                      <div className="col-md-6">
                        <InputField
                          name="creditCardPaymentMethod.cardNumber"
                          label="Credit Card Number"
                          type="text"
                          required
                          placeholder="Enter Card Number"
                          validation={{
                            required: "Card number is required",
                            pattern: {
                              value: /^[0-9]{13,19}$/,
                              message: "Invalid card number",
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <InputField
                          name="creditCardPaymentMethod.billingZipCode"
                          label="Billing Zip Code"
                          type="text"
                          required
                          placeholder="Enter Zip Code"
                          validation={{
                            required: "Zip code is required",
                            pattern: {
                              value: /^\d{5}$/,
                              message: "Zip code must be 5 digits",
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <InputField
                          name="creditCardPaymentMethod.expMonth"
                          label="Expiration Month"
                          type="text"
                          required
                          placeholder="MM"
                          validation={{
                            required: "Expiration month is required",
                            pattern: {
                              value: /^(0[1-9]|1[0-2])$/,
                              message: "Invalid month (01-12)",
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <InputField
                          name="creditCardPaymentMethod.expYear"
                          label="Expiration Year"
                          type="text"
                          required
                          placeholder="YYYY"
                          validation={{
                            required: "Expiration year is required",
                            pattern: {
                              value: /^\d{4}$/,
                              message: "Invalid year",
                            },
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <InputField
                          name="creditCardPaymentMethod.cvvNumber"
                          label="CVV"
                          type="text"
                          required
                          placeholder="Enter CVV"
                          validation={{
                            required: "CVV is required",
                            pattern: {
                              value: /^[0-9]{3,4}$/,
                              message: "Invalid CVV",
                            },
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </form>
            </FormProvider>
          </Card>
        </RightSideModal>
      </div>
    </PermissionGuard>
  );
};

export default PaymentAccountsPage;